import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Drawer,
  Grid2 as Grid,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import FilterListIcon from '@mui/icons-material/FilterList'
import { trace, context } from '@opentelemetry/api'

import ServerTable from './components/ServerTable'
import ServerCards from './components/ServerCards'
import ServerDetailsDialog from './components/ServerDetailsDialog'
import FilterBar from './components/FilterBar'
import AppBar from './components/AppBar'

import Server, { Provider } from './types/Server'
import ServerFilter from './types/ServerFilter'
import { getTracer } from './tracing'
import { PROVIDER_NAMES } from './utils/servers'
import {
  applyFilters,
  availableDiskSizes,
  availableLocations,
  calcInitialFilters,
  countActiveFilters,
} from './utils/filtering'

type EmptyResultProps = { onReset: () => void }

const EmptyResult: React.FC<EmptyResultProps> = ({ onReset }) => (
  <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
    <Typography color="text.secondary" gutterBottom>
      No servers match the current filters.
    </Typography>
    <Button onClick={onReset}>Reset filters</Button>
  </Paper>
)

function App() {
  const [servers, setServers] = useState<Server[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<ServerFilter | null>(null)
  const [initialFilters, setInitialFilters] = useState<ServerFilter | null>(null)
  const [selected, setSelected] = useState<Server | null>(null)
  const [filterOpen, setFilterOpen] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)
  const [fetchedAt, setFetchedAt] = useState<Partial<Record<Provider, string>>>({})

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const loadServers = useCallback(() => {
    setLoading(true)
    setError(null)
    const span = getTracer().startSpan('fetchServers')
    context.with(trace.setSpan(context.active(), span), () => {
      axios
        .get('/api/servers')
        .then((response) => {
          const serverList: unknown = response.data?.server
          if (!Array.isArray(serverList) || serverList.length === 0) {
            throw new Error('Unexpected API response shape')
          }
          setServers(serverList as Server[])
          const updated: unknown = response.data?.last_updated
          setLastUpdated(typeof updated === 'string' ? updated : null)
          const perProvider: Partial<Record<Provider, string>> = {}
          for (const provider of Object.keys(PROVIDER_NAMES) as Provider[]) {
            const at: unknown = response.data?.providers?.[provider]?.last_updated
            if (typeof at === 'string') perProvider[provider] = at
          }
          setFetchedAt(perProvider)
          const init = calcInitialFilters(serverList as Server[])
          setInitialFilters(init)
          setFilters(init)
        })
        .catch((err) => {
          span.recordException(err)
          setError('Could not load the auction data. The backend may be unavailable.')
        })
        .finally(() => {
          span.end()
          setLoading(false)
        })
    })
  }, [])

  useEffect(() => {
    loadServers()
  }, [loadServers])

  const filteredServers = useMemo(
    () => (filters ? applyFilters(servers, filters) : servers),
    [servers, filters]
  )
  const locations = useMemo(() => availableLocations(servers), [servers])
  const diskSizes = useMemo(() => availableDiskSizes(servers), [servers])
  const activeFilters =
    filters && initialFilters ? countActiveFilters(filters, initialFilters) : 0

  const resetFilters = () => initialFilters && setFilters(initialFilters)

  const resultCount = (
    <Typography variant="body2" color="text.secondary">
      {filteredServers.length} of {servers.length} servers
      {lastUpdated && (
        <Tooltip
          title={
            <>
              {Object.entries(fetchedAt).map(([provider, at]) => (
                <div key={provider}>
                  Fetched from {PROVIDER_NAMES[provider as Provider]}:{' '}
                  {new Date(at).toLocaleString()}
                </div>
              ))}
            </>
          }
        >
          <span>
            {' · data from '}
            {new Date(lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </Tooltip>
      )}
    </Typography>
  )

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      <AppBar />
      <Box sx={{ flexGrow: 1, px: { xs: 1, sm: 2 }, pt: 2, pb: 4 }}>
        {loading && <LinearProgress />}
        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadServers}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        {!loading && !error && filters && initialFilters && (
          isMobile ? (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                {resultCount}
                <Badge badgeContent={activeFilters} color="primary">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterOpen(true)}
                  >
                    Filters
                  </Button>
                </Badge>
              </Stack>
              {filteredServers.length === 0 ? (
                <EmptyResult onReset={resetFilters} />
              ) : (
                <ServerCards servers={filteredServers} onSelect={setSelected} />
              )}
              <Drawer
                anchor="bottom"
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                slotProps={{
                  paper: {
                    sx: {
                      maxHeight: '85dvh',
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                      display: 'flex',
                      flexDirection: 'column',
                    },
                  },
                }}
              >
                <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                  <FilterBar
                    filters={filters}
                    initialFilters={initialFilters}
                    setFilters={setFilters}
                    locations={locations}
                    diskSizes={diskSizes}
                  />
                </Box>
                <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
                  <Button fullWidth variant="contained" onClick={() => setFilterOpen(false)}>
                    Show {filteredServers.length} server{filteredServers.length === 1 ? '' : 's'}
                  </Button>
                </Box>
              </Drawer>
            </>
          ) : (
            <Grid container columnSpacing={3}>
              <Grid size={{ md: 4, lg: 3 }}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 16,
                    maxHeight: 'calc(100vh - 32px)',
                    overflowY: 'auto',
                  }}
                >
                  <FilterBar
                    filters={filters}
                    initialFilters={initialFilters}
                    setFilters={setFilters}
                    locations={locations}
                    diskSizes={diskSizes}
                  />
                </Box>
              </Grid>
              <Grid size={{ md: 8, lg: 9 }}>
                <Box sx={{ mb: 1 }}>{resultCount}</Box>
                {filteredServers.length === 0 ? (
                  <EmptyResult onReset={resetFilters} />
                ) : (
                  <ServerTable servers={filteredServers} onSelect={setSelected} />
                )}
              </Grid>
            </Grid>
          )
        )}
      </Box>
      <ServerDetailsDialog server={selected} onClose={() => setSelected(null)} />
    </Container>
  )
}

export default App
